import { useState, useEffect, useMemo, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useLanguage } from '../context/LanguageContext'
import { useAuth } from '../context/AuthContext'
import { Card, CardContent } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Badge } from '../components/ui/badge'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Switch } from '../components/ui/switch'
import { BottomSheet, PickerSheet } from '../components/BottomSheet'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog'
import { 
  Plus, ArrowUpCircle, ArrowDownCircle, Filter, X, Users, RefreshCw,
  Wallet, TrendingUp, Home, Utensils, Car, Heart, GraduationCap, Gamepad2,
  CreditCard, Shirt, BadgeDollarSign, PiggyBank, Gift,
  MoreHorizontal, Briefcase, Building, Zap, Circle, Trash2, ChevronDown, AlertCircle, AlertTriangle,
  Calendar, Check, Clock, CreditCard as CardIcon, Pencil, ChevronLeft, ChevronRight, Eye, Edit, PenLine, ArrowLeft
} from 'lucide-react'
import axios from 'axios'
import { toast } from 'sonner'
import { format, parseISO, startOfMonth, endOfMonth, addMonths } from 'date-fns'
import { es, enUS } from 'date-fns/locale'
import { cn } from '../lib/utils'

const API = `${process.env.REACT_APP_BACKEND_URL}/api`

// Icon mapping for categories
const iconMap = {
  Home, Utensils, Car, Heart, GraduationCap, Gamepad2,
  CreditCard, Shirt, BadgeDollarSign, TrendingUp, PiggyBank, Gift,
  MoreHorizontal, Briefcase, Building, Zap, Circle, Wallet
}

const CURRENCIES = ['MXN', 'USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY', 'CNY', 'BRL', 'ARS', 'COP', 'CLP', 'PEN']

// Haptic feedback helper
const triggerHaptic = () => {
  if (navigator.vibrate) {
    navigator.vibrate(10)
  }
}

export default function SharedFinancePage() {
  const { t, locale, formatCurrency: formatCurrencyIntl } = useLanguage()
  const { token, user } = useAuth()
  const { ownerUserId } = useParams()
  const navigate = useNavigate()
  const dateLocale = locale === 'es-MX' ? es : enUS
  
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [collaboration, setCollaboration] = useState(null)
  const [transactions, setTransactions] = useState([])
  const [categories, setCategories] = useState([])
  const [summary, setSummary] = useState({ income: 0, expense: 0, balance: 0, transaction_count: 0 })
  const [showQuickAdd, setShowQuickAdd] = useState(false)
  const [showFilters, setShowFilters] = useState(false)
  const [filterCreatedByMe, setFilterCreatedByMe] = useState(false)
  const [deleteDialog, setDeleteDialog] = useState({ open: false, transaction: null })
  const [editingTransaction, setEditingTransaction] = useState(null)
  
  // Initialize filters with current month by default
  const [filters, setFilters] = useState(() => {
    const now = new Date()
    const firstDay = startOfMonth(now)
    const lastDay = endOfMonth(now)
    return {
      tipo: '',
      startDate: format(firstDay, 'yyyy-MM-dd'),
      endDate: format(lastDay, 'yyyy-MM-dd')
    }
  })

  // Check if current filter is the current month
  const isCurrentMonth = useMemo(() => {
    const now = new Date()
    const firstDay = format(startOfMonth(now), 'yyyy-MM-dd')
    const lastDay = format(endOfMonth(now), 'yyyy-MM-dd')
    return filters.startDate === firstDay && filters.endDate === lastDay
  }, [filters.startDate, filters.endDate])
  
  // Period label for display
  const periodLabel = useMemo(() => {
    if (!filters.startDate && !filters.endDate) {
      return locale === 'es-MX' ? 'Todo el tiempo' : 'All time'
    }
    
    if (filters.startDate && filters.endDate) {
      const start = parseISO(filters.startDate)
      const end = parseISO(filters.endDate)
      
      const monthStart = startOfMonth(start)
      const monthEnd = endOfMonth(start)
      
      if (format(start, 'yyyy-MM-dd') === format(monthStart, 'yyyy-MM-dd') &&
          format(end, 'yyyy-MM-dd') === format(monthEnd, 'yyyy-MM-dd')) {
        return format(start, 'MMMM yyyy', { locale: dateLocale })
      }
      
      return `${format(start, 'dd MMM', { locale: dateLocale })} - ${format(end, 'dd MMM yyyy', { locale: dateLocale })}`
    }
    
    return locale === 'es-MX' ? 'Periodo personalizado' : 'Custom period'
  }, [filters.startDate, filters.endDate, dateLocale, locale])
  
  // Helper to set filter to a specific month
  const setMonthFilter = (date) => {
    const firstDay = startOfMonth(date)
    const lastDay = endOfMonth(date)
    setFilters(prev => ({
      ...prev,
      startDate: format(firstDay, 'yyyy-MM-dd'),
      endDate: format(lastDay, 'yyyy-MM-dd')
    }))
  }
  
  const goToPreviousMonth = () => {
    const currentStart = filters.startDate ? parseISO(filters.startDate) : new Date()
    setMonthFilter(addMonths(currentStart, -1))
  }
  
  const goToNextMonth = () => {
    const currentStart = filters.startDate ? parseISO(filters.startDate) : new Date()
    setMonthFilter(addMonths(currentStart, 1))
  }
  
  const resetToCurrentMonth = () => {
    setMonthFilter(new Date())
  }

  const fetchData = useCallback(async (showLoader = true) => {
    try {
      if (showLoader) setLoading(true)
      
      // Get categories
      const catRes = await axios.get(
        `${API}/finance/shared/${ownerUserId}/categories`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      setCategories(catRes.data.categories || [])
      setCollaboration(catRes.data.collaboration)
      
      // Build params
      const params = {}
      if (filterCreatedByMe) params.created_by_me = true
      if (filters.startDate) params.start_date = filters.startDate
      if (filters.endDate) params.end_date = filters.endDate
      if (filters.tipo) params.tipo = filters.tipo
      
      // Get transactions
      const txRes = await axios.get(
        `${API}/finance/shared/${ownerUserId}/transactions`,
        { headers: { Authorization: `Bearer ${token}` }, params }
      )
      setTransactions(txRes.data.transactions || [])
      
      // Get summary
      const summaryParams = {
        start_date: filters.startDate,
        end_date: filters.endDate
      }
      if (filterCreatedByMe) summaryParams.created_by_me = true
      
      const sumRes = await axios.get(
        `${API}/finance/shared/${ownerUserId}/summary`,
        { headers: { Authorization: `Bearer ${token}` }, params: summaryParams }
      )
      setSummary({
        income: sumRes.data.total_income || 0,
        expense: sumRes.data.total_expense || 0,
        balance: (sumRes.data.total_income || 0) - (sumRes.data.total_expense || 0),
        transaction_count: sumRes.data.transaction_count || 0
      })
      
    } catch (error) {
      console.error('Error fetching shared finance:', error)
      if (error.response?.status === 403) {
        toast.error(t('collaboration.noAccess'))
        navigate('/shared-with-me')
      } else {
        toast.error(t('common.error'))
      }
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [ownerUserId, token, filterCreatedByMe, filters, t, navigate])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleRefresh = async () => {
    setRefreshing(true)
    await fetchData(false)
  }

  const handleDeleteTransaction = async (id) => {
    try {
      await axios.delete(
        `${API}/finance/shared/${ownerUserId}/transactions/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      toast.success(t('finance.transactionDeleted'))
      setDeleteDialog({ open: false, transaction: null })
      fetchData()
    } catch (error) {
      toast.error(t('finance.errorDeleting'))
    }
  }

  const openDeleteDialog = (tx) => {
    triggerHaptic()
    setDeleteDialog({ open: true, transaction: tx })
  }

  const formatCurrency = (amount, currency = 'MXN') => {
    return formatCurrencyIntl(amount, currency)
  }

  const canCreate = collaboration?.role === 'capturista' || collaboration?.role === 'editor'
  const canEdit = collaboration?.role === 'editor'

  const getRoleLabel = (role) => {
    const labels = {
      viewer: locale === 'es-MX' ? 'Visor' : 'Viewer',
      capturista: 'Capturista',
      editor: locale === 'es-MX' ? 'Editor' : 'Editor'
    }
    return labels[role] || role
  }

  if (loading) {
    return (
      <div className="p-4 md:p-8 pb-safe flex items-center justify-center min-h-[50vh]">
        <div className="animate-pulse text-muted-foreground">{t('common.loading')}</div>
      </div>
    )
  }

  return (
    <div className="p-4 md:p-8 pb-safe max-w-5xl mx-auto">
      {/* Header - Mobile optimized */}
      <div className="flex flex-col gap-3 mb-6">
        {/* Top row: Back button + Title + Actions */}
        <div className="flex items-center gap-3">
          {/* Back button */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/shared-with-me')}
            className="rounded-full h-10 w-10 flex-shrink-0"
            title={locale === 'es-MX' ? 'Volver' : 'Back'}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          
          {/* Title */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl md:text-2xl font-bold">{t('finance.title')}</h1>
              <Badge variant="secondary" className="bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 flex-shrink-0">
                <Users className="w-3 h-3 mr-1" />
                {locale === 'es-MX' ? 'Compartido' : 'Shared'}
              </Badge>
            </div>
            <p className="text-muted-foreground text-xs md:text-sm truncate">
              {collaboration?.owner_name || 'Usuario'} • {getRoleLabel(collaboration?.role)}
            </p>
          </div>
          
          {/* Actions */}
          <div className="flex items-center gap-1 flex-shrink-0">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={handleRefresh}
              disabled={refreshing}
              className="rounded-full h-9 w-9"
              data-testid="refresh-finance-btn"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => setShowFilters(!showFilters)}
              className={cn(
                "rounded-full h-9 w-9",
                showFilters && "bg-primary text-primary-foreground"
              )}
              data-testid="filter-btn"
            >
              <Filter className="w-4 h-4" />
            </Button>
            {canCreate && (
              <Button
                type="button"
                onClick={() => {
                  triggerHaptic()
                  setShowQuickAdd(true)
                }}
                size="sm"
                className="rounded-full h-9 px-3 bg-blue-500 hover:bg-blue-600"
                data-testid="quick-add-btn"
              >
                <Plus className="w-4 h-4" />
                <span className="hidden md:inline ml-1">{locale === 'es-MX' ? 'Nueva' : 'New'}</span>
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Period Selector - Same style as FinanzasPage */}
      <div className="flex items-center justify-between mb-4 bg-secondary/30 rounded-xl p-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={goToPreviousMonth}
          className="rounded-full h-9 w-9 p-0"
          data-testid="prev-month-btn"
        >
          <ChevronLeft className="w-5 h-5" />
        </Button>
        
        <button
          type="button"
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-secondary/50 transition-colors active:bg-secondary/70"
          data-testid="period-selector-btn"
        >
          <Calendar className="w-4 h-4 text-muted-foreground" />
          <span className="font-medium capitalize">{periodLabel}</span>
          {!isCurrentMonth && (
            <Badge variant="outline" className="text-xs ml-1">
              {locale === 'es-MX' ? 'Filtrado' : 'Filtered'}
            </Badge>
          )}
        </button>
        
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={goToNextMonth}
          className="rounded-full h-9 w-9 p-0"
          data-testid="next-month-btn"
        >
          <ChevronRight className="w-5 h-5" />
        </Button>
      </div>

      {/* Summary Cards - Same style as FinanzasPage */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <Card className="bg-emerald-500/10 border-emerald-500/20">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-1">
              <ArrowUpCircle className="w-4 h-4 text-emerald-500" />
              <span className="text-xs text-muted-foreground">{t('finance.income')}</span>
            </div>
            <p className="text-sm font-bold text-emerald-500">{formatCurrency(summary.income)}</p>
          </CardContent>
        </Card>
        
        <Card className="bg-rose-500/10 border-rose-500/20">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-1">
              <ArrowDownCircle className="w-4 h-4 text-rose-500" />
              <span className="text-xs text-muted-foreground">{t('finance.expense')}</span>
            </div>
            <p className="text-sm font-bold text-rose-500">{formatCurrency(summary.expense)}</p>
          </CardContent>
        </Card>
        
        <Card className={cn(
          "border-sky-500/20",
          summary.balance >= 0 ? "bg-sky-500/10" : "bg-amber-500/10"
        )}>
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-1">
              <Wallet className="w-4 h-4 text-sky-500" />
              <span className="text-xs text-muted-foreground">{t('finance.balance')}</span>
            </div>
            <p className={cn(
              "text-sm font-bold",
              summary.balance >= 0 ? "text-sky-500" : "text-amber-500"
            )}>
              {formatCurrency(summary.balance)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters (collapsible) - Same style as FinanzasPage */}
      {showFilters && (
        <Card className="mb-6">
          <CardContent className="p-4 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{t('finance.filters') || 'Filtros'}</span>
              {!isCurrentMonth && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={resetToCurrentMonth}
                  className="rounded-full text-xs"
                  data-testid="reset-month-btn"
                >
                  <Calendar className="w-3 h-3 mr-1" />
                  {locale === 'es-MX' ? 'Mes actual' : 'Current month'}
                </Button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">{t('finance.startDate')}</Label>
                <Input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
                  className="h-10"
                />
              </div>
              <div>
                <Label className="text-xs">{t('finance.endDate')}</Label>
                <Input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
                  className="h-10"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant={filters.tipo === '' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilters(prev => ({ ...prev, tipo: '' }))}
                className="rounded-full"
              >
                {t('common.all')}
              </Button>
              <Button
                variant={filters.tipo === 'income' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilters(prev => ({ ...prev, tipo: 'income' }))}
                className={cn("rounded-full", filters.tipo === 'income' && "bg-emerald-500 hover:bg-emerald-600")}
              >
                <ArrowUpCircle className="w-4 h-4 mr-1" />
                {t('finance.income')}
              </Button>
              <Button
                variant={filters.tipo === 'expense' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilters(prev => ({ ...prev, tipo: 'expense' }))}
                className={cn("rounded-full", filters.tipo === 'expense' && "bg-rose-500 hover:bg-rose-600")}
              >
                <ArrowDownCircle className="w-4 h-4 mr-1" />
                {t('finance.expense')}
              </Button>
            </div>
            {/* My records filter */}
            <div className="flex items-center justify-between pt-2 border-t">
              <Label className="text-sm">{locale === 'es-MX' ? 'Solo mis registros' : 'Only my records'}</Label>
              <Switch
                checked={filterCreatedByMe}
                onCheckedChange={setFilterCreatedByMe}
                data-testid="filter-my-transactions"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Transactions List - Same style as FinanzasPage */}
      {transactions.length === 0 ? (
        <Card className="py-12">
          <CardContent className="text-center">
            <Wallet className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-medium mb-2">{t('finance.noTransactions')}</h3>
            <p className="text-sm text-muted-foreground mb-4">
              {locale === 'es-MX' ? 'Registra tu primera transacción' : 'Record your first transaction'}
            </p>
            {canCreate && (
              <Button onClick={() => setShowQuickAdd(true)} className="rounded-full">
                <Plus className="w-4 h-4 mr-2" />
                {t('finance.addTransaction')}
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {transactions.map((tx) => {
            const IconComponent = tx.categoria_icon ? (iconMap[tx.categoria_icon] || Circle) : Circle
            const isIncome = tx.tipo === 'income'
            
            return (
              <Card 
                key={tx.id} 
                className="group overflow-hidden"
                data-testid={`transaction-${tx.id}`}
              >
                <CardContent className="p-3">
                  <div className="flex items-center gap-3">
                    {/* Category Icon */}
                    <div className={cn(
                      "w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0",
                      isIncome ? "bg-emerald-500/10" : "bg-rose-500/10"
                    )}>
                      <IconComponent className={cn(
                        "w-5 h-5",
                        isIncome ? "text-emerald-500" : "text-rose-500"
                      )} />
                    </div>
                    
                    {/* Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm truncate">
                          {tx.categoria_nombre || (isIncome ? t('finance.income') : t('finance.expense'))}
                        </span>
                        {tx.es_desperdicio && (
                          <Badge variant="outline" className="text-xs border-amber-500/50 text-amber-600">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            {locale === 'es-MX' ? 'Hormiga' : 'Ant'}
                          </Badge>
                        )}
                        {tx.es_inversion && (
                          <Badge variant="outline" className="text-xs border-emerald-500/50 text-emerald-600">
                            <TrendingUp className="w-3 h-3 mr-1" />
                            {locale === 'es-MX' ? 'Inversión' : 'Investment'}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span>{format(parseISO(tx.fecha), 'dd MMM yyyy', { locale: dateLocale })}</span>
                        {tx.metodo && (
                          <>
                            <span>•</span>
                            <span>{tx.metodo}</span>
                          </>
                        )}
                        {tx.created_by_name && tx.created_by_name !== collaboration?.owner_name && (
                          <>
                            <span>•</span>
                            <span className="text-purple-500">{tx.created_by_name}</span>
                          </>
                        )}
                      </div>
                      {tx.notas && (
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{tx.notas}</p>
                      )}
                    </div>
                    
                    {/* Amount & Actions */}
                    <div className="flex items-center gap-2">
                      <span className={cn(
                        "font-bold text-sm",
                        isIncome ? "text-emerald-500" : "text-rose-500"
                      )}>
                        {isIncome ? '+' : '-'}{formatCurrency(tx.monto, tx.moneda)}
                      </span>
                      
                      {canEdit && (
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 rounded-full"
                            onClick={() => setEditingTransaction(tx)}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 rounded-full text-destructive hover:text-destructive"
                            onClick={() => openDeleteDialog(tx)}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}

      {/* Quick Add Transaction Sheet - Same style as FinanzasPage */}
      <QuickAddSheet
        open={showQuickAdd}
        onOpenChange={setShowQuickAdd}
        categories={categories}
        ownerUserId={ownerUserId}
        token={token}
        onSuccess={() => {
          setShowQuickAdd(false)
          fetchData()
        }}
        locale={locale}
        t={t}
        formatCurrency={formatCurrency}
      />

      {/* Edit Transaction Sheet */}
      {editingTransaction && (
        <QuickAddSheet
          open={!!editingTransaction}
          onOpenChange={(open) => !open && setEditingTransaction(null)}
          categories={categories}
          ownerUserId={ownerUserId}
          token={token}
          onSuccess={() => {
            setEditingTransaction(null)
            fetchData()
          }}
          locale={locale}
          t={t}
          formatCurrency={formatCurrency}
          editingTransaction={editingTransaction}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, transaction: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('finance.deleteTransaction')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('finance.deleteConfirmation')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDeleteTransaction(deleteDialog.transaction?.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

// Quick Add Sheet Component - Same as FinanzasPage
function QuickAddSheet({ 
  open, 
  onOpenChange, 
  categories, 
  ownerUserId, 
  token, 
  onSuccess, 
  locale, 
  t, 
  formatCurrency,
  editingTransaction 
}) {
  const [formData, setFormData] = useState({
    tipo: 'expense',
    monto: '',
    moneda: 'MXN',
    categoria_principal: '',
    fecha: new Date().toISOString().split('T')[0],
    metodo: 'Efectivo',
    notas: '',
    es_inversion: false,
    es_desperdicio: false,
    es_msi: false,
    msi_meses: 6,
    msi_fecha_primer_cargo: ''
  })
  const [saving, setSaving] = useState(false)
  const [showCurrencyPicker, setShowCurrencyPicker] = useState(false)
  const [showCategoryPicker, setShowCategoryPicker] = useState(false)
  const [showMethodPicker, setShowMethodPicker] = useState(false)
  const [showMSIMonthsPicker, setShowMSIMonthsPicker] = useState(false)
  
  const MSI_MONTHS_OPTIONS = [3, 6, 9, 12, 18, 24]

  useEffect(() => {
    if (editingTransaction) {
      setFormData({
        tipo: editingTransaction.tipo || 'expense',
        monto: String(editingTransaction.monto || ''),
        moneda: editingTransaction.moneda || 'MXN',
        categoria_principal: editingTransaction.categoria_id || '',
        fecha: editingTransaction.fecha || new Date().toISOString().split('T')[0],
        metodo: editingTransaction.metodo || 'Efectivo',
        notas: editingTransaction.notas || '',
        es_inversion: editingTransaction.es_inversion || false,
        es_desperdicio: editingTransaction.es_desperdicio || false,
        es_msi: editingTransaction.es_msi || false,
        msi_meses: editingTransaction.msi_meses || 6,
        msi_fecha_primer_cargo: editingTransaction.msi_fecha_primer_cargo || ''
      })
    } else {
      setFormData({
        tipo: 'expense',
        monto: '',
        moneda: 'MXN',
        categoria_principal: '',
        fecha: new Date().toISOString().split('T')[0],
        metodo: 'Efectivo',
        notas: '',
        es_inversion: false,
        es_desperdicio: false,
        es_msi: false,
        msi_meses: 6,
        msi_fecha_primer_cargo: ''
      })
    }
  }, [editingTransaction, open])

  const paymentMethods = [
    { value: 'Efectivo', label: locale === 'es-MX' ? 'Efectivo' : 'Cash' },
    { value: 'Tarjeta débito', label: locale === 'es-MX' ? 'Tarjeta débito' : 'Debit card' },
    { value: 'Tarjeta crédito', label: locale === 'es-MX' ? 'Tarjeta crédito' : 'Credit card' },
    { value: 'Transferencia', label: locale === 'es-MX' ? 'Transferencia' : 'Transfer' },
    { value: 'Otro', label: locale === 'es-MX' ? 'Otro' : 'Other' }
  ]

  const handleSubmit = async () => {
    if (!formData.monto || parseFloat(formData.monto) <= 0) {
      toast.error(locale === 'es-MX' ? 'Ingresa un monto válido' : 'Enter a valid amount')
      return
    }

    setSaving(true)
    try {
      const payload = {
        tipo: formData.tipo,
        monto: parseFloat(formData.monto),
        moneda: formData.moneda,
        categoria_principal: formData.categoria_principal || null,
        fecha: formData.fecha,
        metodo: formData.metodo,
        notas: formData.notas,
        es_inversion: formData.es_inversion,
        es_desperdicio: formData.es_desperdicio,
        es_msi: formData.es_msi,
        msi_meses: formData.es_msi ? formData.msi_meses : null,
        msi_fecha_primer_cargo: formData.es_msi && formData.msi_fecha_primer_cargo 
          ? formData.msi_fecha_primer_cargo 
          : null
      }

      if (editingTransaction) {
        await axios.put(
          `${process.env.REACT_APP_BACKEND_URL}/api/finance/shared/${ownerUserId}/transactions/${editingTransaction.id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        )
        toast.success(t('finance.transactionUpdated'))
      } else {
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/finance/shared/${ownerUserId}/transactions`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        )
        toast.success(t('finance.transactionCreated'))
      }
      onSuccess()
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'))
    } finally {
      setSaving(false)
    }
  }

  const selectedCategory = categories.find(c => c.id === formData.categoria_principal)

  return (
    <>
      <BottomSheet isOpen={open} onClose={() => onOpenChange(false)} title={editingTransaction ? (locale === 'es-MX' ? 'Editar transacción' : 'Edit transaction') : (locale === 'es-MX' ? 'Nueva transacción' : 'New transaction')}>
        <div className="p-4 space-y-4">
          {/* Type Selector - Segmented Control */}
          <div className="flex rounded-xl bg-secondary/50 p-1">
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, tipo: 'expense' }))}
              className={cn(
                "flex-1 py-3 rounded-lg text-sm font-medium transition-all",
                formData.tipo === 'expense'
                  ? "bg-rose-500 text-white shadow-sm"
                  : "text-muted-foreground"
              )}
            >
              {t('finance.expense')}
            </button>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, tipo: 'income' }))}
              className={cn(
                "flex-1 py-3 rounded-lg text-sm font-medium transition-all",
                formData.tipo === 'income'
                  ? "bg-emerald-500 text-white shadow-sm"
                  : "text-muted-foreground"
              )}
            >
              {t('finance.income')}
            </button>
          </div>

          {/* Amount Input */}
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">{t('finance.amount')}</Label>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                <Input
                  type="number"
                  inputMode="decimal"
                  value={formData.monto}
                  onChange={(e) => setFormData(prev => ({ ...prev, monto: e.target.value }))}
                  placeholder="0.00"
                  className="pl-7 text-lg h-12"
                />
              </div>
              <Button
                type="button"
                variant="outline"
                className="h-12 px-3"
                onClick={() => setShowCurrencyPicker(true)}
              >
                {formData.moneda}
                <ChevronDown className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>

          {/* Category */}
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">{t('finance.category')}</Label>
            <Button
              type="button"
              variant="outline"
              className="w-full justify-between h-12"
              onClick={() => setShowCategoryPicker(true)}
            >
              <span className={!selectedCategory ? 'text-muted-foreground' : ''}>
                {selectedCategory?.nombre || selectedCategory?.name || (locale === 'es-MX' ? 'Seleccionar categoría' : 'Select category')}
              </span>
              <ChevronDown className="w-4 h-4" />
            </Button>
          </div>

          {/* Date */}
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">{t('finance.date')}</Label>
            <Input
              type="date"
              value={formData.fecha}
              onChange={(e) => setFormData(prev => ({ ...prev, fecha: e.target.value }))}
              className="h-12"
            />
          </div>

          {/* Payment Method */}
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">{t('finance.paymentMethod')}</Label>
            <Button
              type="button"
              variant="outline"
              className="w-full justify-between h-12"
              onClick={() => setShowMethodPicker(true)}
            >
              {formData.metodo}
              <ChevronDown className="w-4 h-4" />
            </Button>
          </div>

          {/* Toggles for expense type */}
          {formData.tipo === 'expense' && (
            <div className="space-y-3 pt-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-emerald-500" />
                  <Label className="text-sm">{locale === 'es-MX' ? 'Es inversión' : 'Is investment'}</Label>
                </div>
                <Switch
                  checked={formData.es_inversion}
                  onCheckedChange={(checked) => setFormData(prev => ({ 
                    ...prev, 
                    es_inversion: checked,
                    es_desperdicio: checked ? false : prev.es_desperdicio
                  }))}
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                  <Label className="text-sm">{locale === 'es-MX' ? 'Gasto hormiga' : 'Ant expense'}</Label>
                </div>
                <Switch
                  checked={formData.es_desperdicio}
                  onCheckedChange={(checked) => setFormData(prev => ({ 
                    ...prev, 
                    es_desperdicio: checked,
                    es_inversion: checked ? false : prev.es_inversion
                  }))}
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-blue-500" />
                  <Label className="text-sm">{locale === 'es-MX' ? '¿MSI (Meses Sin Intereses)?' : 'Interest-free months?'}</Label>
                </div>
                <Switch
                  checked={formData.es_msi}
                  onCheckedChange={(checked) => {
                    triggerHaptic()
                    setFormData(prev => ({ 
                      ...prev, 
                      es_msi: checked,
                      metodo: checked ? 'Tarjeta crédito' : prev.metodo,
                      // Set default first charge date (next month)
                      msi_fecha_primer_cargo: checked && !prev.msi_fecha_primer_cargo
                        ? (() => {
                            const txDate = new Date(prev.fecha || new Date())
                            const nextMonth = new Date(txDate)
                            nextMonth.setMonth(nextMonth.getMonth() + 1)
                            return nextMonth.toISOString().split('T')[0]
                          })()
                        : prev.msi_fecha_primer_cargo
                    }))
                  }}
                />
              </div>
              
              {/* MSI Expanded Fields */}
              {formData.es_msi && (
                <div className="space-y-3 pt-2 border-t border-border/50">
                  {/* MSI Months Selector */}
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">
                      {locale === 'es-MX' ? 'Número de meses' : 'Number of months'}
                    </Label>
                    <button
                      type="button"
                      onClick={() => setShowMSIMonthsPicker(true)}
                      className="w-full h-11 px-4 rounded-lg border bg-background flex items-center justify-between"
                    >
                      <span className="font-medium">{formData.msi_meses} {locale === 'es-MX' ? 'meses' : 'months'}</span>
                      <ChevronDown className="w-4 h-4" />
                    </button>
                  </div>

                  {/* First Charge Date */}
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">
                      {locale === 'es-MX' ? 'Fecha primer cargo' : 'First charge date'}
                    </Label>
                    <Input
                      type="date"
                      value={formData.msi_fecha_primer_cargo}
                      onChange={(e) => setFormData(prev => ({ ...prev, msi_fecha_primer_cargo: e.target.value }))}
                      className="h-11"
                    />
                  </div>

                  {/* MSI Preview */}
                  {formData.monto && parseFloat(formData.monto) > 0 && (
                    <div className="p-3 rounded-lg bg-primary/10 border border-primary/20">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">
                          {locale === 'es-MX' ? 'Monto mensual:' : 'Monthly amount:'}
                        </span>
                        <span className="font-bold text-primary">
                          ${(parseFloat(formData.monto) / formData.msi_meses).toFixed(2)} {formData.moneda}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-xs mt-1">
                        <span className="text-muted-foreground">{locale === 'es-MX' ? 'Total:' : 'Total:'}</span>
                        <span className="font-medium">
                          ${parseFloat(formData.monto).toFixed(2)} {locale === 'es-MX' ? 'en' : 'in'} {formData.msi_meses} {locale === 'es-MX' ? 'pagos' : 'payments'}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Notes */}
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">{t('finance.notes')} ({locale === 'es-MX' ? 'opcional' : 'optional'})</Label>
            <Input
              value={formData.notas}
              onChange={(e) => setFormData(prev => ({ ...prev, notas: e.target.value }))}
              placeholder={locale === 'es-MX' ? 'Agregar nota...' : 'Add note...'}
              className="h-12"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              className="flex-1 h-12 rounded-xl"
              onClick={() => onOpenChange(false)}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              className="flex-1 h-12 rounded-xl bg-black text-white hover:bg-black/90"
              onClick={handleSubmit}
              disabled={saving}
            >
              {saving ? (
                <span className="animate-pulse">{locale === 'es-MX' ? 'Guardando...' : 'Saving...'}</span>
              ) : (
                editingTransaction 
                  ? (locale === 'es-MX' ? 'Actualizar' : 'Update')
                  : (locale === 'es-MX' ? 'Guardar' : 'Save')
              )}
            </Button>
          </div>
        </div>
      </BottomSheet>

      {/* Currency Picker */}
      <PickerSheet
        isOpen={showCurrencyPicker}
        onClose={() => setShowCurrencyPicker(false)}
        title={locale === 'es-MX' ? 'Seleccionar moneda' : 'Select currency'}
      >
        <div className="p-2">
          {CURRENCIES.map(c => (
            <button
              key={c}
              type="button"
              onClick={() => {
                setFormData(prev => ({ ...prev, moneda: c }))
                setShowCurrencyPicker(false)
              }}
              className={cn(
                "w-full text-left px-4 py-3 rounded-lg transition-colors",
                formData.moneda === c ? "bg-primary/10 text-primary" : "hover:bg-secondary"
              )}
            >
              {c}
            </button>
          ))}
        </div>
      </PickerSheet>

      {/* Category Picker - Grid with icon cards (same as FinanzasPage) */}
      <PickerSheet
        isOpen={showCategoryPicker}
        onClose={() => setShowCategoryPicker(false)}
        title={locale === 'es-MX' ? 'Seleccionar categoría' : 'Select category'}
      >
        <div className="p-4">
          {categories.length === 0 ? (
            <div className="py-8 text-center">
              <AlertCircle className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
              <p className="text-muted-foreground font-medium mb-2">
                {locale === 'es-MX' ? 'No hay categorías disponibles' : 'No categories available'}
              </p>
              <p className="text-sm text-muted-foreground">
                {locale === 'es-MX' 
                  ? 'El propietario del espacio debe crear categorías primero.'
                  : 'The space owner must create categories first.'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {categories.map(cat => {
                const IconComponent = cat.icon ? (iconMap[cat.icon] || Circle) : Circle
                const isSelected = formData.categoria_principal === cat.id
                const catName = cat.nombre || cat.name || ''
                const catColor = cat.color || '#6B7280'
                return (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => {
                      triggerHaptic()
                      setFormData(prev => ({ ...prev, categoria_principal: cat.id }))
                      setShowCategoryPicker(false)
                    }}
                    className={cn(
                      "flex flex-col items-center gap-2 p-3 rounded-xl border transition-all",
                      isSelected ? "border-primary bg-primary/10" : "border-border hover:border-primary/50"
                    )}
                  >
                    <div 
                      className="w-10 h-10 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: `${catColor}20` }}
                    >
                      <IconComponent className="w-5 h-5" style={{ color: catColor }} />
                    </div>
                    <span className="text-xs font-medium text-center leading-tight line-clamp-2">{catName}</span>
                  </button>
                )
              })}
            </div>
          )}
        </div>
      </PickerSheet>

      {/* Payment Method Picker */}
      <PickerSheet
        isOpen={showMethodPicker}
        onClose={() => setShowMethodPicker(false)}
        title={locale === 'es-MX' ? 'Método de pago' : 'Payment method'}
      >
        <div className="p-2">
          {paymentMethods.map(m => (
            <button
              key={m.value}
              type="button"
              onClick={() => {
                setFormData(prev => ({ ...prev, metodo: m.value }))
                setShowMethodPicker(false)
              }}
              className={cn(
                "w-full text-left px-4 py-3 rounded-lg transition-colors",
                formData.metodo === m.value ? "bg-primary/10 text-primary" : "hover:bg-secondary"
              )}
            >
              {m.label}
            </button>
          ))}
        </div>
      </PickerSheet>

      {/* MSI Months Picker */}
      <PickerSheet
        isOpen={showMSIMonthsPicker}
        onClose={() => setShowMSIMonthsPicker(false)}
        title={locale === 'es-MX' ? 'Número de meses MSI' : 'Number of MSI months'}
      >
        <div className="grid grid-cols-3 gap-3 p-4">
          {MSI_MONTHS_OPTIONS.map(months => (
            <button
              key={months}
              type="button"
              onClick={() => {
                triggerHaptic()
                setFormData(prev => ({ ...prev, msi_meses: months }))
                setShowMSIMonthsPicker(false)
              }}
              className={cn(
                "p-4 rounded-lg border text-center font-bold transition-all",
                formData.msi_meses === months ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-primary/50"
              )}
            >
              {months} {locale === 'es-MX' ? 'meses' : 'months'}
            </button>
          ))}
        </div>
        {/* Custom months input */}
        <div className="px-4 pb-4">
          <Label className="text-xs text-muted-foreground mb-1 block">
            {locale === 'es-MX' ? 'Otro valor (2-36)' : 'Custom value (2-36)'}
          </Label>
          <Input
            type="number"
            min="2"
            max="36"
            placeholder={locale === 'es-MX' ? 'Ej: 15' : 'Ex: 15'}
            className="h-11"
            onChange={(e) => {
              const val = parseInt(e.target.value)
              if (val >= 2 && val <= 36) {
                triggerHaptic()
                setFormData(prev => ({ ...prev, msi_meses: val }))
                setShowMSIMonthsPicker(false)
              }
            }}
          />
        </div>
      </PickerSheet>
    </>
  )
}
